/**
 * Alta environment detection + sign-in URL construction.
 *
 * Ported from Platinum so this portal sends users to the same sign-in page
 * Platinum's own sibling apps (mailroom, sos, partner center) send them to:
 *   - Platinum `app/utils/domains.ts`              -> getAltaDomain / getEnvironment
 *   - Platinum `app/utils/getControlCenterOrigin.ts` -> origin selection
 *   - Platinum `app/constants/envs.ts`             -> env names + regional subdomains
 *
 * Two davinci-specific differences from Platinum:
 *
 *   1. Hostnames are `wear[.<segment>].alta.avigilon.com` rather than
 *      `control.*` / `mailroom.*`.
 *   2. davinci deploys a `qa` environment (GKE cluster `davinci-qa`); Platinum
 *      does not have a `qa` env at all, so nothing serves a sign-in page at a
 *      `qa` subdomain. `qa` therefore resolves to the `dev` environment, whose
 *      sign-in page does exist. See SEGMENT_TO_OP_ENV.
 */

const ALTA_DOMAIN = 'alta.avigilon.com';

/**
 * Matches `wear.alta.avigilon.com` (no segment) and
 * `wear.<segment>.alta.avigilon.com`, capturing `<segment>` when present.
 *
 * Anchored at both ends on purpose: an un-anchored match would accept a
 * lookalike host such as `alta.avigilon.com.attacker.example`, and we would
 * then read the auth cookie name and sign-in origin off an untrusted domain.
 */
const ALTA_HOSTNAME_REGEX = /^[^.]+\.(?:([^.]+)\.)?alta\.avigilon\.com$/;

/**
 * URL segment in front of `alta.avigilon.com` -> OP_ENV.
 *
 * `qa` maps to `dev` deliberately (see file header): Platinum owns the sign-in
 * page and has no qa deployment, so redirecting a qa user to a qa sign-in host
 * would send them to a hostname that does not resolve.
 */
const SEGMENT_TO_OP_ENV = {
  dev: 'dev',
  qa: 'dev',
  sandbox: 'sandbox',
  eu: 'prodeu1',
  can: 'prodcan1',
  aus: 'prodaus1',
};

/**
 * OP_ENV -> the URL segment Alta uses for that env. Production has no segment,
 * which is why prod is the empty string rather than `'prod'`.
 *
 * This is the inverse of SEGMENT_TO_OP_ENV for every env except `qa`, which has
 * no Alta segment of its own because it resolves to `dev`.
 *
 * Mirrors Platinum's REGIONAL_PROD_SUBDOMAINS + TOKEN_SUFFIX in
 * `app/constants/envs.ts` and `app/utils/accessToken.ts`.
 */
const OP_ENV_TO_SEGMENT = {
  prod: '',
  dev: 'dev',
  sandbox: 'sandbox',
  prodeu1: 'eu',
  prodcan1: 'can',
  prodaus1: 'aus',
};

/**
 * Envs whose Helium API still answers on openpath.com rather than
 * alta.avigilon.com. Copied from Platinum's LEGACY_OPENPATH_HELIUM_URLS
 * (`app/constants/envs.ts`); this service's backend agrees, defaulting
 * `alta-access.base-url` to `https://helium.dev.openpath.com`
 * (`src/main/resources/application.properties`).
 */
const LEGACY_OPENPATH_HELIUM_URLS = {
  dev: 'https://helium.dev.openpath.com',
  sandbox: 'https://helium.sandbox.openpath.com',
  prod: 'https://helium.prod.openpath.com',
  prodeu1: 'https://helium.prodeu1.openpath.com',
};

/** Envs where Platinum, not Access, serves the sign-in page. */
const PLATINUM_SIGNIN_ENVS = ['dev', 'sandbox'];

const getHostname = () =>
  typeof window === 'undefined' ? '' : window.location.hostname;

/**
 * Resolves OP_ENV from the browser hostname.
 *
 * Returns `undefined` for any host that is not `*.alta.avigilon.com` —
 * localhost, the `*.gcp-dev.calipsa.biz` hosts, preview builds. Callers must
 * treat `undefined` as "not an Alta deployment" and skip the token check
 * entirely, otherwise local development (which authenticates with the
 * dev-login session cookie, not an Alta token) would redirect on every load.
 *
 * @param {string} [hostname] Defaults to `window.location.hostname`.
 * @returns {'prod'|'dev'|'sandbox'|'prodeu1'|'prodcan1'|'prodaus1'|undefined}
 */
export const getOpEnv = (hostname = getHostname()) => {
  const match = ALTA_HOSTNAME_REGEX.exec(hostname);
  if (!match) return undefined;

  const segment = match[1];

  // No segment -> production, e.g. `wear.alta.avigilon.com`.
  if (!segment) return 'prod';

  // An unrecognised segment is not assumed to be prod: falling back to prod
  // would point a new environment at the production sign-in page and read the
  // production cookie name.
  return SEGMENT_TO_OP_ENV[segment];
};

/**
 * The env-specific Alta domain, e.g. `dev.alta.avigilon.com`, or
 * `alta.avigilon.com` for production.
 *
 * @param {string} opEnv
 * @returns {string|undefined} undefined when opEnv is unknown.
 */
export const getAltaDomain = (opEnv) => {
  const segment = OP_ENV_TO_SEGMENT[opEnv];
  if (segment === undefined) return undefined;

  return segment ? `${segment}.${ALTA_DOMAIN}` : ALTA_DOMAIN;
};

/**
 * Origin that serves the sign-in page for an env.
 *
 * Platinum's rule, copied verbatim from `getControlCenterOrigin.ts` and its
 * test: dev and sandbox are served from `platinum.<domain>`, every other env
 * from `access.<domain>`. Platinum's own test asserts that a sibling app on
 * dev resolves to `https://platinum.dev.alta.avigilon.com`, which is why this
 * is not `access.dev.alta.avigilon.com`.
 *
 * @param {string} opEnv
 * @returns {string|undefined}
 */
export const getSignInOrigin = (opEnv) => {
  const domain = getAltaDomain(opEnv);
  if (!domain) return undefined;

  const subdomain = PLATINUM_SIGNIN_ENVS.includes(opEnv)
    ? 'platinum'
    : 'access';

  return `https://${subdomain}.${domain}`;
};

/**
 * Full sign-in URL, including the `redirectUrl` that returns the user here
 * after they authenticate.
 *
 * `redirectUrl` is percent-encoded. Platinum interpolates the raw href
 * (`?redirectUrl=${href}`), which truncates the value at the first `&` when
 * the current URL carries its own query string — the portal's list pages do.
 *
 * @param {string} opEnv
 * @param {string} [currentHref] URL to come back to. Omit for no redirectUrl.
 * @returns {string|undefined}
 */
export const getSignInUrl = (opEnv, currentHref) => {
  const origin = getSignInOrigin(opEnv);
  if (!origin) return undefined;

  const signInUrl = `${origin}/signin`;
  return currentHref
    ? `${signInUrl}?redirectUrl=${encodeURIComponent(currentHref)}`
    : signInUrl;
};

/**
 * Name of the cookie holding the Alta access token for an env.
 *
 * Keyed on OP_ENV rather than the raw hostname segment, which matters for qa:
 * a browser on `wear.qa.alta.avigilon.com` must read `accessToken-dev`, because
 * qa resolves to the dev environment and Platinum never writes an
 * `accessToken-qa`. The cookie is readable there because Platinum scopes it to
 * `.alta.avigilon.com` rather than to a single host.
 *
 * Mirrors Platinum's getAccessToken in `app/utils/accessToken.ts`.
 *
 * @param {string} opEnv
 * @returns {string|undefined}
 */
export const getAccessTokenCookieName = (opEnv) => {
  const segment = OP_ENV_TO_SEGMENT[opEnv];
  if (segment === undefined) return undefined;

  return segment ? `accessToken-${segment}` : 'accessToken';
};

/**
 * Helium API origin for an env — the API that validates the access token.
 *
 * `VITE_HELIUM_BASE_URL` overrides the derived value. That escape hatch exists
 * because the browser calls Helium cross-origin: if Helium does not send
 * `Access-Control-Allow-Origin` for a `wear.*` host, the validate request fails
 * preflight, and the override lets an env point at a same-origin nginx proxy
 * instead (the pattern `portal-ui/nginx.conf` already uses for `/pair-api/`).
 *
 * @param {string} opEnv
 * @returns {string|undefined}
 */
export const getHeliumBaseUrl = (opEnv) => {
  const override = import.meta.env?.VITE_HELIUM_BASE_URL;
  if (override) return override.replace(/\/$/, '');

  if (LEGACY_OPENPATH_HELIUM_URLS[opEnv]) {
    return LEGACY_OPENPATH_HELIUM_URLS[opEnv];
  }

  // Newer regions land on alta.avigilon.com. Note this keys off the full env
  // name, not the URL segment: Helium hosts use `prodcan1` while the
  // user-facing apps use `can`.
  return OP_ENV_TO_SEGMENT[opEnv] === undefined
    ? undefined
    : `https://helium.${opEnv}.${ALTA_DOMAIN}`;
};
